import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  Search,
  X,
  History,
  RotateCcw,
  Calendar,
  MapPin,
  Check,
} from "lucide-react";
import RefuelLoader from "../components/Refuelloader.jsx";
import { useNavigate } from "react-router-dom";
import api, { IMAGE_BASE_URL, ADMIN_IMAGE_URL } from "../api/Api";

const RefuelHistoryPage = () => {
  const navigate = useNavigate();

  // --- States ---
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search Query State
  const [searchQuery, setSearchQuery] = useState("");

  // Date States (Default: Last 3 Days)
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 3);
    return d.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split("T")[0];
  });

  const [selectedLog, setSelectedLog] = useState(null);
  const [fullImage, setFullImage] = useState(null);

  // --- Fetch Data ---
  useEffect(() => {
    fetchHistory();
  }, []);

  // --- Live Filter Effect ---
  useEffect(() => {
    applyFilter(logs);
  }, [logs, startDate, endDate, searchQuery]);

  const fetchHistory = async () => {
    const driverRegNo = localStorage.getItem("driverRegNo");
    if (!driverRegNo) return;

    setLoading(true);
    try {
      const response = await api.get(`/refuel/history/${driverRegNo}`);
      if (response.data.success) {
        const allData = response.data.data;
        setLogs(allData);
      }
    } catch (error) {
      console.error("History fetch error", error);
    } finally {
      setLoading(false);
    }
  };

  // --- Robust Filter Logic ---
  const applyFilter = (data) => {
    if (!data) return;

    const filtered = data.filter((log) => {
      // 1. Date Range Check
      const logDate = new Date(log.created_at);
      logDate.setHours(0, 0, 0, 0);

      const sDate = startDate ? new Date(startDate) : null;
      if (sDate) sDate.setHours(0, 0, 0, 0);

      const eDate = endDate ? new Date(endDate) : null;
      if (eDate) eDate.setHours(0, 0, 0, 0);

      const isDateMatch =
        (!sDate || logDate >= sDate) && (!eDate || logDate <= eDate);

      // 2. Search Query Check (Station Name OR Amount)
      let isSearchMatch = true;
      if (searchQuery && searchQuery.trim() !== "") {
        const q = searchQuery.toLowerCase().trim();
        const station = log.station_name ? log.station_name.toLowerCase() : "";
        const amount = log.amount ? log.amount.toString() : "";

        isSearchMatch = station.includes(q) || amount.includes(q);
      }

      return isDateMatch && isSearchMatch;
    });

    setFilteredLogs(filtered);
  };

  const clearFilters = () => {
    const today = new Date().toISOString().split("T")[0];
    setStartDate("");
    setEndDate(today);
    setSearchQuery("");
  };

  // const getImageUrl = (filename) => {
  //   if (!filename) return "";
  //   return `${IMAGE_BASE_URL}/uploads/refuel_receipt/${filename}`;
  // };
  const getImageUrl = (filename, type) => {
    if (!filename) {
      return "https://placehold.co/400?text=No+Img";
    }
    let finalUrl = "";
    if (type === "payment_proof") {
      finalUrl = `${ADMIN_IMAGE_URL}/uploads/refuel_receipt/${filename}`;
    } else {
      finalUrl = `${IMAGE_BASE_URL}/uploads/refuel_receipt/${filename}`;
    }
    // console.log("🔗 FULL URL:", finalUrl);
    return finalUrl;
  };

if (loading) {
    return (
      <div className="loader-container">
        <RefuelLoader />
        <style>{`
            .loader-container {
                position: fixed; 
                top: 0;
                left: 0;
                width: 100%;
                height: 100%; 
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                background: #ffffff;
            }
        `}</style>
      </div>
    );
  }

  return (
    <div className="container-fluid p-0 bg-light min-vh-100 font-native pb-5">
      {/* --- HEADER --- */}
    <div
        className="shadow-sm overflow-hidden" 
        style={{
          background: "linear-gradient(90deg, #e46d28 0%, #5610a5 100%)",
          borderBottomLeftRadius: "24px",
          borderBottomRightRadius: "24px"
        }}
      >
        {/* Top Bar - Centered & Arrow Removed */}
        <div className="d-flex flex-column align-items-center justify-content-center pt-3 pb-3 text-white text-center">
          <h4 className="fw-bolder mb-2 text-white tracking-tight" style={{ letterSpacing: "0.5px" }}>
            REFUEL ACTIVITY
          </h4>
          <span 
            className="badge rounded-pill px-3 py-2 fw-medium shadow-sm" 
            style={{ 
              fontSize: "0.8rem", 
              backgroundColor: "rgba(255, 255, 255, 0.15)",
              backdropFilter: "blur(8px)",
              border: "1px solid rgba(255, 255, 255, 0.2)"
            }}
          >
            {filteredLogs.length} Records Found
          </span>
        </div>

        {/* --- PREMIUM SEARCH & FILTER SECTION --- */}
        <div className="px-2 pb-2">
          <div className="bg-white p-3 shadow-sm" style={{ borderRadius: "20px" }}>
            
            {/* 1. Premium Search Bar */}
            <div className="position-relative w-100 mb-3">
              <div
                className="position-absolute top-50 start-0 translate-middle-y ps-3 text-primary"
                style={{ pointerEvents: "none" }}
              >
                <Search size={20} strokeWidth={2.5} />
              </div>
              <input
                type="text"
                className="form-control border-0 rounded-pill ps-5 fw-medium text-dark"
                placeholder="Search Station or Amount..."
                style={{ 
                  fontSize: "0.95rem", 
                  backgroundColor: "#F3F4F6", 
                  height: "48px", 
                  boxShadow: "inset 0 2px 4px rgba(0,0,0,0.02)"
                }}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="btn border-0 position-absolute top-50 end-0 translate-middle-y pe-2 text-secondary d-flex align-items-center"
                >
                  <span className="bg-white rounded-circle shadow-sm d-flex align-items-center justify-content-center" style={{ width: '28px', height: '28px' }}>
                    <X size={16} className="text-dark" strokeWidth={3} />
                  </span>
                </button>
              )}
            </div>

            <div className="d-flex align-items-stretch gap-2 w-100">
              
              {/* From Date */}
              <div className="flex-fill" style={{ minWidth: 0 }}>
                <div
                  className="position-relative border-0 px-2 py-2 h-100 d-flex flex-column justify-content-center"
                  style={{ backgroundColor: "#F8FAFC", borderRadius: "14px" }}
                >
                  <label
                    className="text-primary fw-bold mb-0 d-block"
                    style={{ fontSize: "10px", letterSpacing: "0.5px" }}
                  >
                    FROM
                  </label>
                  <input
                    type="date"
                    className="form-control border-0 bg-transparent shadow-none p-0 fw-semibold text-dark w-100"
                    style={{ fontSize: "0.8rem" }}
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
              </div>

              {/* To Date */}
              <div className="flex-fill" style={{ minWidth: 0 }}>
                <div
                  className="position-relative border-0 px-2 py-2 h-100 d-flex flex-column justify-content-center"
                  style={{ backgroundColor: "#F8FAFC", borderRadius: "14px" }}
                >
                  <label
                    className="text-primary fw-bold mb-0 d-block"
                    style={{ fontSize: "10px", letterSpacing: "0.5px" }}
                  >
                    TO
                  </label>
                  <input
                    type="date"
                    className="form-control border-0 bg-transparent shadow-none p-0 fw-semibold text-dark w-100"
                    style={{ fontSize: "0.8rem" }}
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>

              {/* Clear Button */}
              {(startDate ||
                endDate !== new Date().toISOString().split("T")[0] ||
                searchQuery) && (
                <div className="flex-shrink-0">
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="btn border-0 d-flex align-items-center justify-content-center shadow-sm h-100"
                    style={{ 
                      width: "44px", // Firmly fixed width
                      backgroundColor: "#FEF2F2",
                      color: "#EF4444", 
                      borderRadius: "14px",
                      transition: "transform 0.1s ease",
                      padding: 0
                    }}
                    onMouseDown={(e) => e.currentTarget.style.transform = "scale(0.95)"}
                    onMouseUp={(e) => e.currentTarget.style.transform = "scale(1)"}
                  >
                    <RotateCcw size={20} strokeWidth={2.5} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* --- LIST SECTION --- */}
      <div className="px-3 pt-3 d-flex flex-column gap-3">
        {filteredLogs.length === 0 ? (
          <div className="text-center py-5 mt-5 text-muted opacity-50">
            <History size={56} strokeWidth={1} className="mb-3" />
            <p className="fs-6">No records found matching your filters.</p>
            <button
              className="btn btn-sm btn-outline-primary rounded-pill px-4"
              onClick={clearFilters}
            >
              Clear Filters
            </button>
          </div>
        ) : (
          filteredLogs.map((log) => (
            <div
              key={log.id}
              onClick={() => setSelectedLog(log)}
              className="card border-0 shadow-sm rounded-4 overflow-hidden active-scale-down cursor-pointer bg-white"
            >
              {/* Comfortable Row Layout */}
              <div className="d-flex align-items-center p-3">
                {/* 1. Status Indicator */}
                <div
                  className={`rounded-pill bg-${log.status_color}`}
                  style={{
                    width: "5px",
                    height: "45px",
                    marginRight: "14px",
                    flexShrink: 0,
                  }}
                ></div>

                {/* 2. Main Info */}
                <div
                  className="flex-grow-1 overflow-hidden"
                  style={{ minWidth: 0 }}
                >
                  <h6
                    className="fw-bold mb-1 text-dark text-truncate text-uppercase"
                    style={{ fontSize: "0.95rem" }}
                  >
                    {log.station_name}
                  </h6>
                  <div
                    className="d-flex align-items-center gap-2 text-muted"
                    style={{ fontSize: "0.75rem" }}
                  >
                    <Calendar size={12} />
                    <span>
                      {new Date(log.created_at).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                    <span className="opacity-50">|</span>
                    <span>
                      {new Date(log.created_at).toLocaleTimeString("en-US", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </span>
                  </div>
                  <div className="d-flex flex-wrap gap-1 mt-1">
                    {Number(log.paid_thru) === 16 && (
                      <span
                        className="badge bg-info-subtle text-success border border-info-subtle"
                        style={{ fontSize: "0.65rem" }}
                      >
                        CASH PAYMENT
                      </span>
                    )}

                    {log.internal_notes && (
                      <span
                        className="badge bg-info-subtle text-success border border-info-subtle text-truncate"
                        style={{ maxWidth: "120px", fontSize: "0.65rem" }}
                        title={log.internal_notes}
                      >
                        {log.internal_notes}
                      </span>
                    )}
                  </div>
                </div>

                {/* 3. Right Side (Amount & Badge) */}
                <div className="text-end ps-2 flex-shrink-0">
                  <h6
                    className="fw-bold mb-1 text-dark"
                    style={{ fontSize: "1rem" }}
                  >
                    ₹{Math.round(log.amount)}
                  </h6>
                  <span
                    className={`badge bg-${log.status_color}-subtle text-${log.status_color} border border-${log.status_color}-subtle px-2 py-1 rounded-pill`}
                    style={{ fontSize: "0.65rem", fontWeight: 600 }}
                  >
                    {log.status}
                  </span>
                </div>
              </div>

              {/* Optional: Tiny Footer for Liters */}
              <div
                className="bg-light px-3 py-2 border-top d-flex justify-content-between align-items-center"
                style={{ fontSize: "0.75rem" }}
              >
                <span className="text-muted fw-medium">
                  Volume:{" "}
                  <span className="text-dark fw-bold">{log.liters} L</span>
                </span>
                <span className="text-dark">{log.expense_no}</span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* ============ MODAL (Details) ============ */}
      {selectedLog && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.75)", 
            zIndex: 9999, 
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
            transform: "translateZ(0)",
            WebkitTransform: "translateZ(0)",
          }}
          onClick={() => setSelectedLog(null)}
        >
          <div
            className="bg-white w-100 shadow-lg animate-slide-up"
            style={{
              maxWidth: "500px",
              maxHeight: "92%",
              overflowY: "auto",
              borderTopLeftRadius: "28px",
              borderTopRightRadius: "28px",
              position: "relative",
              zIndex: 10000,
              transform: "translateZ(0)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Draggable Handle */}
            <div className="d-flex justify-content-center pt-3 pb-2">
              <div
                className="bg-secondary opacity-25 rounded-pill"
                style={{ width: "45px", height: "5px" }}
              ></div>
            </div>

            {/* Modal Header */}
            <div className="px-4 pb-3 d-flex justify-content-between align-items-center border-bottom border-light-subtle sticky-top bg-white">
              <div>
                <h5 className="fw-bold mb-0 text-dark">Refuel Details</h5>
                <small
                  className="text-primary text-uppercase fw-bold ls-1"
                  style={{ fontSize: "0.75rem" }}
                >
                  {selectedLog.expense_no}
                </small>
              </div>
              <button
                className="btn btn-light rounded-circle p-2 shadow-sm"
                onClick={() => setSelectedLog(null)}
              >
                <X size={22} />
              </button>
            </div>

            <div className="px-4 pb-4 pt-3">
              {/* Hero Amount Card */}
              <div
                className="p-4 rounded-4 mb-4 text-white shadow-sm position-relative overflow-hidden"
                style={{
                  background:
                    "linear-gradient(135deg, #10636e 0%, #2b2183 100%)",
                }}
              >
                <div className="d-flex justify-content-between align-items-center position-relative z-1">
                  <div>
                    <span
                      className="opacity-75 small fw-bold text-uppercase"
                      style={{ fontSize: "0.7rem" }}
                    >
                      Total Paid
                    </span>
                    <h1 className="fw-bold mb-0 display-6">
                      ₹{selectedLog.amount}
                    </h1>
                  </div>
                  <span
                    className={`badge rounded-pill px-3 py-2 border bg-white text-${selectedLog.status_color} fw-bold shadow-sm`}
                  >
                    {selectedLog.status}
                  </span>
                </div>
              </div>

              {/* Information Grid */}
              <div className="bg-light rounded-4 p-3 mb-4 border border-light-subtle shadow-sm">
                <div className="row g-3">
                  <div className="col-12 border-bottom pb-3">
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <MapPin size={14} className="text-primary" />
                      <small
                        className="text-muted fw-bold text-uppercase"
                        style={{ fontSize: "0.7rem" }}
                      >
                        Station Location
                      </small>
                    </div>
                    <span className="fw-bold text-dark d-block">
                      {selectedLog.station_name}
                    </span>
                  </div>
                  <div className="col-6 border-end">
                    <small
                      className="text-muted d-block fw-bold mb-1 text-uppercase"
                      style={{ fontSize: "0.7rem" }}
                    >
                      Volume
                    </small>
                    <span className="fw-bold text-dark fs-6">
                      {selectedLog.liters} L
                    </span>
                  </div>
                  <div className="col-6">
                    <small
                      className="text-muted d-block fw-bold mb-1 text-uppercase"
                      style={{ fontSize: "0.7rem" }}
                    >
                      Rate/L
                    </small>
                    <span className="fw-bold text-dark fs-6">
                      ₹{selectedLog.price_per_liter}
                    </span>
                  </div>
                  <div className="col-12 pt-1">
                    <small
                      className="text-muted d-block fw-bold mb-1 text-uppercase"
                      style={{ fontSize: "0.7rem" }}
                    >
                      Date & Time
                    </small>
                    <span className="fw-bold text-dark">
                    {new Date(selectedLog.created_at.replace(' ', 'T')).toLocaleString()}
                  </span>
                  </div>
                </div>
              </div>

              {/* Section: Driver Evidence */}
              <h6 className="fw-bold text-primary small mb-3 text-uppercase ls-1">
                Driver Uploads
              </h6>
              <div className="row g-2 mb-4">
                {[
                  { img: selectedLog.fuel_meter_image, label: "PUMP" },
                  { img: selectedLog.tank_image, label: "TANK" },
                  { img: selectedLog.oil_meter_image, label: "ODO" },
                  { img: selectedLog.qr_image, label: "QR", isQR: true },
                ].map(
                  (item, idx) =>
                    item.img && (
                      <div className="col-3" key={idx}>
                        <div
                          className="ratio ratio-1x1 rounded-3 overflow-hidden border cursor-pointer bg-light shadow-sm position-relative"
                          onClick={() =>
                            setFullImage(getImageUrl(item.img, "driver_upload"))
                          }
                        >
                          <img
                            src={getImageUrl(item.img, "driver_upload")}
                            className={`w-100 h-100 ${item.isQR ? "object-fit-contain p-1 bg-white" : "object-fit-cover"}`}
                            alt={item.label}
                          />
                        </div>
                        <div
                          className="text-center small fw-bold mt-1 text-muted text-uppercase"
                          style={{ fontSize: "10px" }}
                        >
                          {item.label}
                        </div>
                      </div>
                    ),
                )}
              </div>

              {/* ================= SECTION: PAYMENT VERIFICATION (Robust Block) ================= */}
              {(selectedLog.payment_proof_image ||
                selectedLog.internal_notes) && (
                <div className="mb-4">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <h6 className="fw-bold mb-0 text-success small text-uppercase ls-1">
                      Payment Verification
                    </h6>
                  </div>

                  <div
                    className="p-3 rounded-4 border border-success-subtle shadow-sm"
                    style={{ background: "rgba(25, 135, 84, 0.04)" }}
                  >
                    <div className="row g-3 align-items-center">
                      {/* Proof Thumbnail */}
                      {selectedLog.payment_proof_image && (
                        <div className="col-4">
                          <div
                            className="ratio ratio-1x1 rounded-3 overflow-hidden border border-success border-opacity-25 cursor-pointer shadow-sm position-relative"
                            onClick={() =>
                              setFullImage(
                                getImageUrl(
                                  selectedLog.payment_proof_image,
                                  "payment_proof",
                                ),
                              )
                            }
                          >
                            <img
                              src={getImageUrl(
                                selectedLog.payment_proof_image,
                                "payment_proof",
                              )}
                              className="w-100 h-100 object-fit-cover"
                              alt="Receipt"
                              onError={(e) =>
                                (e.target.src =
                                  "https://placehold.co/200?text=Proof")
                              }
                            />
                            <div className="position-absolute top-0 end-0 bg-dark opacity-50 text-white p-1 rounded-bottom-start">
                              <Check size={10} />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Note Content */}
                      <div
                        className={
                          selectedLog.payment_proof_image ? "col-8" : "col-12"
                        }
                      >
                        <div className="d-flex flex-column gap-1">
                          <span
                            className="text-success small fw-bold text-uppercase"
                            style={{ fontSize: "9px", letterSpacing: "0.5px" }}
                          >
                            Confirmation Comments
                          </span>
                          <div className="bg-white p-2 rounded-3 border border-success-subtle shadow-sm">
                            <p
                              className="mb-0 text-dark fw-bold small fst-italic"
                              style={{ lineHeight: "1.4", fontSize: "0.85rem" }}
                            >
                              {selectedLog.internal_notes}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <button
                className="btn btn-dark w-100 rounded-pill py-3 fw-bold shadow-lg mt-2"
                onClick={() => setSelectedLog(null)}
              >
                Close Details
              </button>
            </div>
          </div>
        </div>
      )}

      {/* LIGHTBOX */}
      {fullImage && (
        <div
          className="fixed-top w-100 h-100 bg-black d-flex align-items-center justify-content-center p-0"
          style={{zIndex: 99999, 
            transform: "translateZ(0)",
            WebkitTransform: "translateZ(0)" }}
          onClick={() => setFullImage(null)}
        >
          <button className="position-absolute top-0 end-0 m-4 btn btn-dark rounded-circle p-3 z-3 bg-opacity-50 border-0">
            <X size={24} className="text-white" />
          </button>
          <img
            src={fullImage}
            alt="Full View"
            className="img-fluid"
            style={{
              maxHeight: "100vh",
              maxWidth: "100vw",
              objectFit: "contain",
            }}
          />
        </div>
      )}

      <style>{`
        .font-native { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
        .active-scale:active { transform: scale(0.97); transition: 0.1s; }
        .active-scale-down:active { transform: scale(0.98); transition: 0.1s; }
        
        .animate-slide-up { 
           animation: slideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
           -webkit-animation: slideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        @keyframes slideUp { 
          from { 
            transform: translateY(100%); 
            -webkit-transform: translateY(100%);
          } 
          to { 
            transform: translateY(0); 
            -webkit-transform: translateY(0);
          } 
        }
        .form-control:focus { box-shadow: none; background-color: #ffffff !important; }
      `}</style>
    </div>
  );
};

export default RefuelHistoryPage;